<script setup lang="ts" generic="TStudent extends Components.Schemas.StudentDto | Components.Schemas.BaseStudentDto">
import { Components } from '@/api/generated/api';
import { useSchoolYears } from '@/composables/useSchoolYears';
import DisplayName from '@/core/DisplayName.vue';
import IconButton from '@/core/elements/IconButton.vue';
import ClassOrGroupNameList from '@/core/elements/ClassOrGroupNameList.vue';
import UserAvatar from '@/core/UserAvatar.vue';
import { EIcon } from 'common/enum/misc.enum';
import { computed, toRefs } from 'vue';
import { getSchoolClassesForYear } from '@/utils/schoolClass.utils';

const { currentSchoolYear } = useSchoolYears();

const emit = defineEmits<{
  'click:add': [value: TStudent];
  'click:remove': [value: TStudent];
}>();

const props = defineProps<{
  student: TStudent;
  type: 'add' | 'remove';
  disabled?: boolean;
  surface?: 'primary' | 'secondary';
}>();
const { student, type, disabled, surface } = toRefs(props);

const studentClasses = computed<Components.Schemas.SchoolClassDto[] | null>(() =>
  'schoolClasses' in student.value
    ? getSchoolClassesForYear(student.value.schoolClasses, currentSchoolYear.value?.id)
    : null,
);

const surfaceClass = computed<string>(() => {
  return surface.value === 'primary' ? 'bg-surface-primary' : 'bg-surface-secondary';
});
</script>

<template>
  <div class="list-item-base" :class="surfaceClass">
    <UserAvatar class="mr-md !size-button" :user="student" />
    <div class="flex flex-col flex-grow">
      <div class="text-wrap break-all w-full font-semibold">
        <DisplayName :person="student" />
      </div>
      <ClassOrGroupNameList v-if="studentClasses?.length" :school-classes="studentClasses" />
    </div>
    <IconButton
      v-if="type === 'add' && !disabled"
      :icon-name="EIcon.PLUS"
      theme="primary-filled"
      class="h-button w-button"
      @click="emit('click:add', student)"
    />
    <IconButton
      v-if="type === 'remove' && !disabled"
      :icon-name="EIcon.X"
      theme="secondary"
      class="h-button w-button"
      @click="emit('click:remove', student)"
    />
  </div>
</template>
